import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, LogOut, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Cursor from "../components/Cursor";

const TOKEN_KEY = "at_admin_token";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const errText = (err, fallback) => {
  const d = err.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

const Gate = ({ onUnlock }) => {
  const [passcode, setPasscode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!passcode) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { passcode });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onUnlock(data.token);
    } catch (err) {
      toast(errText(err, "Incorrect passcode"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-gate">
        <div className="eyebrow mb-6">Private</div>
        <h1 className="font-display font-extrabold text-[40px] leading-none t-fg tracking-[-0.03em] mb-12">
          The desk<span className="t-terra">.</span>
        </h1>
        <input
          type="password"
          value={passcode}
          onChange={(e) => setPasscode(e.target.value)}
          className="input-line"
          placeholder="Passcode"
          autoFocus
          data-testid="admin-passcode-input"
        />
        <button type="submit" disabled={busy} className="btn-pill mt-10 disabled:opacity-60" data-testid="admin-login-btn">
          {busy ? "Checking…" : "Unlock"}
          <ArrowRight size={14} strokeWidth={1.8} />
        </button>
        <div className="mt-14">
          <Link to="/" className="text-[13px] t-dim link-underline">← Back to the site</Link>
        </div>
      </form>
    </div>
  );
};

const Dash = ({ token, onLogout }) => {
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState({ title: "", tag: "", body: "" });
  const [busy, setBusy] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const handle401 = useCallback(
    (err) => {
      if (err.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        onLogout();
        return true;
      }
      return false;
    },
    [onLogout]
  );

  const load = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([api.get("/posts"), api.get("/contact", auth)]);
      setPosts(p.data);
      setMessages(m.data);
    } catch (err) {
      if (!handle401(err)) toast("Could not load data");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const publish = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) {
      toast("Title and body are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/posts", form, auth);
      toast("Published.");
      setForm({ title: "", tag: "", body: "" });
      load();
    } catch (err) {
      if (!handle401(err)) toast(errText(err, "Could not publish"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/posts/${id}`, auth);
      setPosts((ps) => ps.filter((p) => p.id !== id));
      toast("Deleted.");
    } catch (err) {
      if (!handle401(err)) toast("Could not delete");
    }
  };

  return (
    <div className="min-h-screen px-6 md:px-12 py-10 max-w-[1100px] mx-auto" data-testid="admin-dash">
      <div className="flex items-center justify-between mb-14">
        <div>
          <div className="eyebrow mb-2">The desk</div>
          <h1 className="font-display font-extrabold text-[28px] t-fg tracking-[-0.03em]">
            Thoughts &amp; News<span className="t-terra">.</span>
          </h1>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/" className="text-[13px] t-dim link-underline">View site</Link>
          <button
            onClick={() => { sessionStorage.removeItem(TOKEN_KEY); onLogout(); }}
            className="flex items-center gap-2 text-[13px] t-dim hover:t-fg transition-colors"
            data-testid="admin-logout"
          >
            <LogOut size={14} strokeWidth={1.6} /> Lock
          </button>
        </div>
      </div>

      <div className="flex gap-8 mb-12">
        {[
          { id: "posts", label: `Posts (${posts.length})` },
          { id: "messages", label: `Messages (${messages.length})` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`text-[14px] font-medium pb-2 border-b transition-colors ${
              tab === t.id ? "t-fg border-[#C84B31]" : "t-dim border-transparent hover:t-fg"
            }`}
            data-testid={`admin-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "posts" ? (
        <>
          <form onSubmit={publish} className="space-y-6 mb-20" data-testid="admin-post-form">
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="input-line"
              placeholder="Title"
              data-testid="admin-post-title"
            />
            <input
              type="text"
              value={form.tag}
              onChange={(e) => setForm({ ...form, tag: e.target.value })}
              className="input-line"
              placeholder="Tag, e.g. From the field, Thinking, News (optional)"
              data-testid="admin-post-tag"
            />
            <textarea
              rows={5}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="input-line resize-none"
              placeholder="Write the thought…"
              data-testid="admin-post-body"
            />
            <button type="submit" disabled={busy} className="btn-pill disabled:opacity-60" data-testid="admin-publish-btn">
              {busy ? "Publishing…" : "Publish"}
              <ArrowRight size={14} strokeWidth={1.8} />
            </button>
          </form>

          <div>
            {posts.map((p) => (
              <div
                key={p.id}
                className="flex items-start justify-between gap-6 py-6 border-t"
                style={{ borderColor: "var(--hair-soft)" }}
                data-testid={`admin-post-item-${p.id}`}
              >
                <div>
                  <div className="flex items-center gap-3 text-[12px] t-faint mb-1.5">
                    <span>{fmtDate(p.created_at)}</span>
                    {p.tag && <span className="tag-pill">{p.tag}</span>}
                  </div>
                  <div className="font-display font-bold text-[18px] t-fg">{p.title}</div>
                  <p className="text-[14px] t-dim mt-2 line-clamp-2 max-w-[70ch]">{p.body}</p>
                </div>
                <button
                  onClick={() => remove(p.id)}
                  className="flex-shrink-0 t-faint hover:text-terra transition-colors mt-1"
                  aria-label="Delete post"
                  data-testid={`admin-delete-${p.id}`}
                >
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div data-testid="admin-messages">
          {messages.length === 0 ? (
            <div className="t-dim text-[15px]">No messages yet.</div>
          ) : (
            messages.map((m) => (
              <div
                key={m.id}
                className="py-6 border-t"
                style={{ borderColor: "var(--hair-soft)" }}
                data-testid={`admin-message-${m.id}`}
              >
                <div className="text-[12px] t-faint mb-1.5">{fmtDate(m.created_at)}</div>
                <div className="font-display font-bold text-[16px] t-fg">
                  {m.name} <span className="t-dim font-sans font-normal text-[14px]">· {m.email}</span>
                </div>
                <p className="text-[14px] t-dim mt-2 max-w-[75ch] whitespace-pre-line">{m.message}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));

  return (
    <div className="sec-dark min-h-screen">
      <Cursor />
      {token ? (
        <Dash token={token} onLogout={() => setToken(null)} />
      ) : (
        <Gate onUnlock={setToken} />
      )}
    </div>
  );
}
